import * as React from 'react'
import { X } from '@phosphor-icons/react'
import { Avatar, type AvatarProps } from './avatar'
import { cn } from '../../lib/utils'

/**
 * Notification — composable primitives for a notification panel / dropdown
 * (NotificationPanel · Header · Heading · List · Item · Title · Text ·
 * Description · Meta · Icon · Dot · Footer), from the Figma "Notifications"
 * specs (nodes 11598-28460 / 12148-34799) + the TailGrids notification model.
 *
 * These are low-level parts, not a single mega-prop component: the Figma's
 * notification layouts are all rebuilt by composing them — the same approach as
 * <Alert>. Pair them with <Button>, <Badge>, and the <Tab*> filters as needed.
 *
 * For rows, two presets encode the leading-visual convention — person-driven
 * notifications use an avatar (<NotificationPersonItem>), event-driven ones use
 * a tinted icon (<NotificationEventItem>) — so a single feed can freely mix
 * both. Drop to the bare <NotificationItem> for a custom leading.
 *
 * Colors are First Bite tokens straight off the Figma variables: title
 * (gray-800), body (gray-700), and secondary (gray-500) text; gray-100/200
 * borders; and a basil-500 unread dot (the Figma "brand" accent). Tinted leading
 * visuals use the -50 soft surfaces (the same surfaces <Badge> uses) rather than
 * an /opacity modifier — `fbui-*` tokens are bare `var()`s, so `/10` can't tint
 * them.
 */

/* ── Panel (card shell) ─────────────────────────────────────────────────── */

export type NotificationPanelProps = React.HTMLAttributes<HTMLDivElement>

export const NotificationPanel = React.forwardRef<
  HTMLDivElement,
  NotificationPanelProps
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="notification-panel"
    className={cn(
      'flex flex-col rounded-2xl border border-fbui-gray-200 bg-fbui-base-white shadow-fbui-lg',
      className
    )}
    {...props}
  />
))
NotificationPanel.displayName = 'NotificationPanel'

export interface NotificationHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Render a bottom hairline separating the header from the list. */
  divider?: boolean
}

export const NotificationHeader = React.forwardRef<
  HTMLDivElement,
  NotificationHeaderProps
>(({ className, divider = false, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="notification-header"
    className={cn(
      'flex items-center justify-between gap-2 px-5 py-4',
      divider && 'border-b border-fbui-gray-100',
      className
    )}
    {...props}
  />
))
NotificationHeader.displayName = 'NotificationHeader'

export interface NotificationHeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Heading level (1–6). Defaults to 3. */
  level?: 1 | 2 | 3 | 4 | 5 | 6
}

export const NotificationHeading = React.forwardRef<
  HTMLHeadingElement,
  NotificationHeadingProps
>(({ className, level = 3, ...props }, ref) => {
  const Heading = `h${level}` as const
  return (
    <Heading
      ref={ref}
      data-slot="notification-heading"
      className={cn(
        'flex items-center gap-2 text-lg font-semibold tracking-[-0.2px] text-fbui-text-title',
        className
      )}
      {...props}
    />
  )
})
NotificationHeading.displayName = 'NotificationHeading'

export type NotificationCloseProps =
  React.ButtonHTMLAttributes<HTMLButtonElement>

/**
 * Dismiss (×) button for the panel header. Matches the borderless close
 * affordance shared by Alert / Toast / Badge — a neutral gray X with an
 * opacity/color hover, not the chromed <Button iconOnly> — so dismiss looks the
 * same everywhere. Defaults `aria-label` to "Close"; pass `onClick` to wire it.
 */
export const NotificationClose = React.forwardRef<
  HTMLButtonElement,
  NotificationCloseProps
>(({ className, 'aria-label': ariaLabel = 'Close', ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={ariaLabel}
    data-slot="notification-close"
    className={cn(
      '-m-1 inline-flex shrink-0 items-center justify-center rounded-full p-1 text-fbui-gray-500 outline-none transition-colors hover:text-fbui-gray-700 focus-visible:ring-2 focus-visible:ring-fbui-ring-default',
      className
    )}
    {...props}
  >
    <X weight="bold" className="size-5" aria-hidden="true" />
  </button>
))
NotificationClose.displayName = 'NotificationClose'

/* ── List ───────────────────────────────────────────────────────────────── */

export interface NotificationListProps extends React.HTMLAttributes<HTMLUListElement> {
  /**
   * Scroll the list within the panel: caps the height (defaults to `max-h-96`,
   * override with a `max-h-*` className) and adds a thin scrollbar. `min-h-0`
   * lets it shrink inside a flex parent so the header/footer stay pinned.
   */
  scroll?: boolean
  /** Draw hairlines between items (the divided-feed layouts). */
  divided?: boolean
}

export const NotificationList = React.forwardRef<
  HTMLUListElement,
  NotificationListProps
>(({ className, scroll = false, divided = false, ...props }, ref) => (
  <ul
    ref={ref}
    data-slot="notification-list"
    className={cn(
      'flex flex-col',
      divided && 'divide-y divide-fbui-gray-100',
      scroll &&
        'max-h-96 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-fbui-gray-200 [&::-webkit-scrollbar-track]:bg-transparent',
      className
    )}
    {...props}
  />
))
NotificationList.displayName = 'NotificationList'

/* ── Item ───────────────────────────────────────────────────────────────── */

export interface NotificationItemProps extends React.LiHTMLAttributes<HTMLLIElement> {
  /** Leading visual — an <Avatar>, <NotificationIcon>, or <NotificationDot>. */
  leading?: React.ReactNode
  /** Trailing column (right-aligned), e.g. a timestamp + unread dot. */
  trailing?: React.ReactNode
  /**
   * Show the corner unread dot (basil). Ignored when `trailing` is set — put the
   * unread indicator inside the trailing column instead, so the two never overlap.
   */
  unread?: boolean
  /**
   * Add a hover + focus affordance (background, pointer, focus-visible ring) for
   * clickable rows. The ring only shows once the row is focusable — when wiring
   * `onClick`, also give the row `tabIndex={0}` + an `onKeyDown` handler, or wrap
   * its content in an `<a>`/`<button>`, so keyboard users can reach it.
   */
  interactive?: boolean
}

export const NotificationItem = React.forwardRef<
  HTMLLIElement,
  NotificationItemProps
>(
  (
    {
      className,
      leading,
      trailing,
      unread = false,
      interactive = false,
      children,
      ...props
    },
    ref
  ) => (
    <li
      ref={ref}
      data-slot="notification-item"
      className={cn(
        'relative flex gap-3 px-3 py-3',
        interactive &&
          'cursor-pointer rounded-lg transition-colors hover:bg-fbui-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fbui-ring-default',
        className
      )}
      {...props}
    >
      {leading != null && <div className="shrink-0">{leading}</div>}
      <div className="flex min-w-0 flex-1 flex-col gap-1">{children}</div>
      {trailing != null && (
        <div className="flex shrink-0 flex-col items-end gap-2">{trailing}</div>
      )}
      {unread && trailing == null && (
        <span
          role="img"
          aria-label="Unread"
          className="absolute right-3.5 top-3.5 size-2 shrink-0 rounded-full bg-fbui-basil-500"
        />
      )}
    </li>
  )
)
NotificationItem.displayName = 'NotificationItem'

/* ── Text parts ─────────────────────────────────────────────────────────── */

export type NotificationTitleProps = React.HTMLAttributes<HTMLParagraphElement>

/**
 * Primary line. Defaults to the emphasized title weight/color (the common
 * "**Bold title**" case); wrap the de-emphasized runs of an actor sentence in
 * <NotificationText> ("**Name** requested access").
 */
export const NotificationTitle = React.forwardRef<
  HTMLParagraphElement,
  NotificationTitleProps
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    data-slot="notification-title"
    className={cn(
      'text-sm font-medium leading-snug tracking-[-0.2px] text-fbui-text-title',
      className
    )}
    {...props}
  />
))
NotificationTitle.displayName = 'NotificationTitle'

export type NotificationTextProps = React.HTMLAttributes<HTMLSpanElement>

/** De-emphasized inline run inside a NotificationTitle (regular weight, body color). */
export const NotificationText = React.forwardRef<
  HTMLSpanElement,
  NotificationTextProps
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    data-slot="notification-text"
    className={cn('font-normal text-fbui-text-body', className)}
    {...props}
  />
))
NotificationText.displayName = 'NotificationText'

export type NotificationDescriptionProps =
  React.HTMLAttributes<HTMLParagraphElement>

/** Secondary message line beneath the title. */
export const NotificationDescription = React.forwardRef<
  HTMLParagraphElement,
  NotificationDescriptionProps
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    data-slot="notification-description"
    className={cn('text-sm tracking-[-0.2px] text-fbui-text-body', className)}
    {...props}
  />
))
NotificationDescription.displayName = 'NotificationDescription'

export type NotificationMetaProps = React.HTMLAttributes<HTMLDivElement>

/**
 * Metadata row (category, timestamp, …). Children are joined by a small bullet
 * separator automatically — pass each datum as its own child.
 */
export const NotificationMeta = React.forwardRef<
  HTMLDivElement,
  NotificationMetaProps
>(({ className, children, ...props }, ref) => {
  // toArray already drops null/undefined/booleans and assigns keys.
  const items = React.Children.toArray(children)
  return (
    <div
      ref={ref}
      data-slot="notification-meta"
      className={cn(
        'flex items-center gap-2 text-xs tracking-[-0.2px] text-fbui-text-secondary',
        className
      )}
      {...props}
    >
      {items.map((child, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <span
              aria-hidden="true"
              className="size-1 shrink-0 rounded-full bg-fbui-gray-300"
            />
          )}
          {child}
        </React.Fragment>
      ))}
    </div>
  )
})
NotificationMeta.displayName = 'NotificationMeta'

/* ── Leading visuals ────────────────────────────────────────────────────── */

export type NotificationTone =
  | 'neutral'
  | 'brand'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'orange'
  | 'purple'
  | 'pink'
  | 'cyan'

/** Soft-surface + accent pair for the icon circle (mirrors the Badge palette). */
const iconToneCls: Record<NotificationTone, string> = {
  neutral: 'bg-fbui-gray-100 text-fbui-gray-500',
  brand: 'bg-fbui-basil-50 text-fbui-basil-600',
  success: 'bg-fbui-green-50 text-fbui-green-600',
  warning: 'bg-fbui-amber-50 text-fbui-amber-600',
  error: 'bg-fbui-red-50 text-fbui-red-600',
  info: 'bg-fbui-sky-50 text-fbui-sky-600',
  orange: 'bg-fbui-orange-50 text-fbui-orange-500',
  purple: 'bg-fbui-purple-50 text-fbui-purple-600',
  pink: 'bg-fbui-pink-50 text-fbui-pink-600',
  cyan: 'bg-fbui-cyan-50 text-fbui-cyan-600',
}

/** Solid accent shade for the status dot. */
const dotToneCls: Record<NotificationTone, string> = {
  neutral: 'bg-fbui-gray-300',
  brand: 'bg-fbui-basil-500',
  success: 'bg-fbui-green-500',
  warning: 'bg-fbui-amber-500',
  error: 'bg-fbui-red-500',
  info: 'bg-fbui-sky-500',
  orange: 'bg-fbui-orange-500',
  purple: 'bg-fbui-purple-500',
  pink: 'bg-fbui-pink-500',
  cyan: 'bg-fbui-cyan-500',
}

export interface NotificationIconProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'color'
> {
  /** Color family for the soft circle + icon accent. */
  tone?: NotificationTone
}

/** Circular tinted icon container (12×12, 24px icon) for category-style feeds. */
export const NotificationIcon = React.forwardRef<
  HTMLSpanElement,
  NotificationIconProps
>(({ className, tone = 'neutral', children, ...props }, ref) => (
  <span
    ref={ref}
    data-slot="notification-icon"
    aria-hidden="true"
    className={cn(
      'flex size-12 items-center justify-center rounded-full [&>svg]:size-6',
      iconToneCls[tone],
      className
    )}
    {...props}
  >
    {children}
  </span>
))
NotificationIcon.displayName = 'NotificationIcon'

export interface NotificationDotProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'color'
> {
  /** Color family for the dot. */
  tone?: NotificationTone
}

/** Small solid status dot (used as a leading marker in dotted feeds). */
export const NotificationDot = React.forwardRef<
  HTMLSpanElement,
  NotificationDotProps
>(({ className, tone = 'neutral', ...props }, ref) => (
  <span
    ref={ref}
    data-slot="notification-dot"
    aria-hidden="true"
    className={cn(
      'mt-1.5 inline-block size-2.5 shrink-0 rounded-full',
      dotToneCls[tone],
      className
    )}
    {...props}
  />
))
NotificationDot.displayName = 'NotificationDot'

/* ── Preset rows (person / event) ───────────────────────────────────────────
 * Two thin <NotificationItem> wrappers that encode the leading-visual
 * convention so feeds can freely mix the two: a notification about a *person's*
 * action carries their avatar; a notification about an *event* (an order, a
 * system change) carries a tinted icon. Content (title/description/meta/actions)
 * is identical and passed as children either way. */

export interface NotificationPersonItemProps extends Omit<
  NotificationItemProps,
  'leading'
> {
  /**
   * Avatar config for the person behind the notification (the leading visual):
   * `name` drives the initials + alt text, plus optional `src` / `status`.
   * Defaults to size "lg"; override via `avatar.size`.
   */
  avatar: AvatarProps
}

/** Person-driven row — an action by someone, shown with their avatar. */
export const NotificationPersonItem = React.forwardRef<
  HTMLLIElement,
  NotificationPersonItemProps
>(({ avatar, ...props }, ref) => (
  <NotificationItem
    ref={ref}
    leading={<Avatar size="lg" {...avatar} />}
    {...props}
  />
))
NotificationPersonItem.displayName = 'NotificationPersonItem'

export interface NotificationEventItemProps extends Omit<
  NotificationItemProps,
  'leading'
> {
  /** Color family for the tinted icon circle. */
  tone?: NotificationTone
  /** The event icon, rendered inside the circle. */
  icon: React.ReactNode
}

/** Event-driven row — a system/object event, shown with a tinted icon. */
export const NotificationEventItem = React.forwardRef<
  HTMLLIElement,
  NotificationEventItemProps
>(({ tone, icon, ...props }, ref) => (
  <NotificationItem
    ref={ref}
    leading={<NotificationIcon tone={tone}>{icon}</NotificationIcon>}
    {...props}
  />
))
NotificationEventItem.displayName = 'NotificationEventItem'

/* ── Footer ─────────────────────────────────────────────────────────────── */

export interface NotificationFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Render a top hairline separating the footer from the list. */
  divider?: boolean
}

export const NotificationFooter = React.forwardRef<
  HTMLDivElement,
  NotificationFooterProps
>(({ className, divider = false, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="notification-footer"
    className={cn(
      'flex items-center justify-between gap-2 px-5 py-4',
      divider && 'border-t border-fbui-gray-100',
      className
    )}
    {...props}
  />
))
NotificationFooter.displayName = 'NotificationFooter'
